import type { TemplateResult } from 'lit';
import { css, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { styleMap } from 'lit/directives/style-map.js';
import { FileViewerDialog } from 'src/layout/parts/file-viewer-dialog.js';
import { BaseListSectionElement } from 'src/library/lists/base-list-section-element.js';
import * as dabihStore from 'src/store';
import { sortedByName } from '../../store/utilities.js';
import '../elements/d-action.js';
import '../elements/d-label.js';
import '../elements/d-progress-bar.js';
import './d-list-header.js';
import { DListSection } from './d-list-section.js';
import 'src/library/fields/d-view-info';
import type { ListSectionItemInput } from './listSectionItemInput';
import type { UploadItem } from './upload-item';
import type { AttachmentItem } from './attachment-item';
import type { FileViewerInput } from '../../layout/parts/file-viewer-input';
import type { ActionInput } from '../elements/action-input';
import { sanitizeFilename } from 'src/utilities/filename-sanitation';
import { t } from 'src/i18n';

/**
 *
 */
@customElement('d-list-section-attachment')
export class DListSectionAttachment extends BaseListSectionElement<AttachmentItem> {
  static readonly styles = [
    ...DListSection.styles,
    css`
      :host {
        display: block;
        padding-bottom: 11px;
      }

      input {
        position: absolute;
        width: 0;
        height: 0;
        opacity: 0;
      }

      .uploading {
        margin-left: var(--listPaddingLeft);
      }

      .uploading > div {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
      }

      d-progress-bar {
        width: 120px;
        height: 6px;
        border-radius: 3px;
        margin-right: 12px;
      }

      d-list-section-item {
        flex: 1;
      }

      @media only screen and (max-width: 600px) {
        .files-wrapper,
        .uploading {
          margin-left: 0;
        }
      }

      d-view-info {
        margin-top: 12px;
        border: 1px solid var(--borderColor);
        border-radius: 6px;
        padding: 12px;
        background: hsl(0, 0%, 96%);
      }
    `,
  ];
  attachmentsBefore: any;
  u: any;
  @property({ type: String })
  field = 'generalFields_attachments';
  @property({ type: String })
  icon = 'attachments';
  @property({ type: String })
  url = '';
  @property({ type: Boolean, attribute: 'write-access', reflect: true })
  writeAccess = false;
  @property({ type: Boolean })
  emergencyRestrictions = false;
  @property({ type: String })
  token = '';
  @property({ type: String })
  pageId = '';
  @property({ type: String })
  allowedTypes = '*';
  @property({ type: Array })
  uploadList: UploadItem[] = [];
  @property({ type: String })
  entityType = '';

  private get actions(): ActionInput[] {
    if (this.writeAccess && !this.emergencyRestrictions) {
      return [
        {
          name: t('attachments.upload'),
          action: 'upload',
          slot: 'top-right',
        },
      ];
    }
    return [];
  }

  _handleFilePick(e) {
    e.stopPropagation();
    e.preventDefault();
    const target = e.target as HTMLInputElement;
    if (target.files !== null) {
      const files: File[] = [...target.files];
      this.u = files.length;
      this.attachmentsBefore = this.items.length;
      const sanitizedFiles = files.map((f) => {
        const sanitizedName = sanitizeFilename(f.name);
        return new File([f], sanitizedName, { type: f.type, lastModified: f.lastModified });
      });

      for (const file of sanitizedFiles) {
        this.uploadList = [...this.uploadList, { name: file.name, progress: 0, file: file }];
        this.dispatchEvent(
          new CustomEvent<{ file: File; callback: () => void }>('upload-file', {
            bubbles: true,
            composed: true,
            detail: {
              file: file,
              callback: () => {
                this.uploadList = this.uploadList.filter((f) => f.file !== file);
              },
            },
          }),
        );
      }
    }
  }

  _computeHref(url, item) {
    return dabihStore.BASE_PATH + '/organizations/' + url + '/attachments/' + encodeURIComponent(item);
  }

  _isDisabled(pageId, writeAccess) {
    return pageId === 223117 || !writeAccess;
  }

  _isEmptyAndInTrash(pageId, attachments) {
    return pageId === 223117 && attachments.length < 1;
  }

  _attachmentsLength(attachments) {
    return attachments.length;
  }

  async onAttachmentItemClick(item: AttachmentItem) {
    const input: FileViewerInput = {
      document: await item.asDocumentItem(),
      writeAccess: this.writeAccess && !this.emergencyRestrictions,
      refresh: () => item.asDocumentItem(),
    };
    await FileViewerDialog.open(input);
  }

  _handleActions(e) {
    if (e.detail === 'upload' && this.shadowRoot) {
      let input;
      if (this.shadowRoot.getElementById('input')) {
        input = this.shadowRoot.getElementById('input') as HTMLInputElement;
        if (input) {
          input.click();
        }
      }
    }
  }

  renderHeader(): TemplateResult | typeof nothing {
    const listHeaderStyles = { top: this.contentStickyTop - 1 + 'px' };
    return html`
      <d-list-header
        icon="attachments"
        field="generalFields_attachments"
        .actions=${this.actions}
        @action=${(e) => this._handleActions(e)}
        style=${styleMap(listHeaderStyles)}
        ?theme-page=${this.themePage}
        ?map-element=${this.mapElement}
      >
      </d-list-header>
    `;
  }

  renderItem(item: AttachmentItem): TemplateResult | typeof nothing {
    const asListItem = this.toListSectionItemInput(item);
    return html`<d-list-section-item
      .input=${asListItem}
      @click=${() => this.onAttachmentItemClick(item)}
    ></d-list-section-item>`;
  }

  renderContractMessage(): TemplateResult | typeof nothing {
    if (this.entityType === 'employees' || this.entityType === 'partners') {
      const employeeAttachmentInfo = t('attachments.employeeAttachmentInfo');
      return html` <d-view-info .content="${employeeAttachmentInfo}"></d-view-info>`;
    }
    return nothing;
  }

  renderPostContent(): TemplateResult | typeof nothing {
    return this._isDisabled(this.pageId, this.writeAccess)
      ? nothing
      : html`
          <div class="uploading">
            ${sortedByName(this.uploadList).map((item) => {
              const asListItem: ListSectionItemInput = {
                secondaryLabel: '',
                updateStatus: 'none',
                accessible: true,
                label: item.name,
              };
              return html`<div>
                <d-progress-bar total="100" .partNumber=${item.progress}></d-progress-bar
                ><d-list-section-item no-left-padding .input=${asListItem}></d-list-section-item>
              </div>`;
            })}
          </div>
          <input
            id="input"
            accept="${this.allowedTypes}"
            multiple=""
            @change=${(e) => this._handleFilePick(e)}
            type="file"
          />
          ${this.renderContractMessage()}
        `;
  }

  private fileName(name) {
    const n = name.lastIndexOf('.');
    return n > 0 ? name.slice(0, n) : name;
  }

  private fileExtension(attachment) {
    const p = attachment.fullPath;
    const ext = p.substring(p.lastIndexOf('.') + 1);
    return '.' + ext.toLowerCase();
  }

  private toListSectionItemInput(attachment: AttachmentItem): ListSectionItemInput {
    return {
      secondaryLabel: '',
      label: this.fileName(attachment.name) + this.fileExtension(attachment),
      accessible: true,
      updateStatus: 'none',
    };
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-list-section-attachment': DListSectionAttachment;
  }
}
